// /codex — public codex of the memory.
//
// 8 lore terms (The Eldsky, The Shattering, The Shards, Embers, The
// Awakening, The Bond, Memory Shards, Echoforms) presented as a
// single scrollable bestiary fragment. Spoiler-light by design —
// defines what each term IS without revealing the per-bond narratives
// the Memory Shards surface privately.
//
// Public in Phase A (no beforeLoad gating). The closed door is
// reframed in-world by the footer ("The door is not yet open. The
// Embers return for those who stay.") so the locked /awakening reads
// as IP intention rather than friction.
//
// Each entry has an `id` matching its key so anchor links work and
// the URL is shareable as athrelaris.com/codex#shattering.

import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandMark } from '../components/BrandMark';

export const Route = createFileRoute('/codex')({
  component: CodexPage,
});

type TermKey =
  | 'eldsky'
  | 'shattering'
  | 'shards'
  | 'embers'
  | 'awakening'
  | 'bond'
  | 'memoryShards'
  | 'echoforms';

interface TermSpec {
  key: TermKey;
  /** Public path under apps/web/public/landing/codex/ */
  imageSrc: string;
}

// Order is editorial: cosmogony first (Eldsky → Shattering → Shards),
// then the central beings (Embers), then the human encounter
// (Awakening → Bond → Memory Shards), and Echoforms last as the
// "shapes the Ember can wear" — closes by widening the lens.
const TERMS: TermSpec[] = [
  { key: 'eldsky', imageSrc: '/landing/codex/eldsky.png' },
  { key: 'shattering', imageSrc: '/landing/codex/shattering.png' },
  { key: 'shards', imageSrc: '/landing/codex/shards.png' },
  { key: 'embers', imageSrc: '/landing/codex/embers.png' },
  { key: 'awakening', imageSrc: '/landing/codex/awakening.png' },
  { key: 'bond', imageSrc: '/landing/codex/bond.png' },
  { key: 'memoryShards', imageSrc: '/landing/codex/memory-shards.png' },
  { key: 'echoforms', imageSrc: '/landing/codex/echoforms.png' },
];

function CodexPage() {
  return (
    <div className="-mx-6 -mt-10 sm:-mx-10 sm:-mt-14 lg:-mx-12 lg:-mt-16">
      <CodexHero />
      {TERMS.map((t, i) => (
        <CodexEntry key={t.key} term={t} index={i} />
      ))}
      <CodexFooter />
    </div>
  );
}

function CodexHero() {
  const { t } = useTranslation();
  return (
    <section className="relative flex min-h-[50vh] flex-col items-center justify-center overflow-hidden px-6 py-16 text-center sm:px-10 sm:py-20">
      <BrandMark />
      <p className="mt-10 font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.5em] sm:text-xs lg:text-sm">
        {t('codex.eyebrow')}
      </p>
      <h1
        className="mt-6 max-w-3xl font-display font-medium text-ash-50 text-4xl leading-tight tracking-wide sm:text-5xl lg:text-6xl lg:leading-[1.1]"
        style={{ fontWeight: 500 }}
      >
        {t('codex.heading')}
      </h1>
      <p
        className="mt-8 max-w-2xl font-body text-ash-300 text-base italic leading-relaxed sm:text-lg lg:mt-10 lg:text-xl"
        style={{ animation: 'fadeUp 1.6s ease-out 0.4s both' }}
      >
        {t('codex.subtitle')}
      </p>
      {/* In-world provenance: the codex is a fragmentary, anonymous,
       * many-handed text. This justifies why entries about pre-
       * Shattering things (Eldsky) coexist with contemporary
       * phenomena (Memory Shards, Echoforms) — different pages,
       * different hands, different ages. Keeps the lore coherent
       * without inventing a single canonical narrator. */}
      <p
        className="mt-6 max-w-2xl font-body text-ash-400 text-sm italic leading-relaxed sm:text-base lg:mt-8 lg:text-lg"
        style={{ animation: 'fadeUp 1.6s ease-out 0.7s both' }}
      >
        {t('codex.provenance')}
      </p>
      <p className="mt-6 max-w-xl font-body text-ash-500 text-sm italic leading-relaxed">
        {t('codex.introBody')}
      </p>

      <p className="mt-12 font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.4em] lg:mt-14">
        {t('codex.indexLabel')}
      </p>
      <ul className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-3 sm:gap-x-8">
        {TERMS.map((term) => (
          <li key={term.key}>
            <a
              href={`#${term.key}`}
              className="font-body text-ash-400 text-sm italic transition hover:text-ash-100 sm:text-base"
            >
              {t(`codex.terms.${term.key}.name`)}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CodexEntry({ term, index }: { term: TermSpec; index: number }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);

  // Same reveal mechanism as the landing BeatSection — fade + rise on
  // first intersection, one-shot. Lets the bestiary entries breathe
  // into view rather than appear all at once on first scroll.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.disconnect();
          }
        }
      },
      { threshold: 0.2 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const reverse = index % 2 === 1;
  const name = t(`codex.terms.${term.key}.name`);
  const tagline = t(`codex.terms.${term.key}.tagline`);
  const body = t(`codex.terms.${term.key}.body`);
  const closing = t(`codex.terms.${term.key}.closing`);
  const alt = t(`codex.terms.${term.key}.alt`);

  return (
    <section
      ref={ref}
      id={term.key}
      className={`landing-reveal flex flex-col items-center px-6 py-8 sm:px-10 sm:py-12 lg:flex-row lg:gap-x-12 lg:px-12 lg:py-16 ${
        reverse ? 'lg:flex-row-reverse' : ''
      }`}
    >
      <figure className="relative w-full max-w-xs flex-shrink-0 lg:w-1/2 lg:max-w-sm">
        <div
          aria-hidden="true"
          className="-inset-6 absolute animate-softGlow rounded-full bg-gradient-radial from-accent/15 via-transparent to-transparent blur-3xl"
        />
        {/* Dark placeholder shape behind the image so a missing PNG
         *  doesn't render a broken-image icon — visitors see a quiet
         *  dark plate while authoring is in progress. The mask hides
         *  the rectangular edges to keep the bestiary aesthetic. */}
        <div
          aria-hidden="true"
          className="landing-ember-mask absolute inset-0 bg-paper-900/40"
        />
        <img
          src={term.imageSrc}
          alt={alt}
          loading="lazy"
          className="landing-ember-mask relative w-full select-none"
          draggable={false}
        />
      </figure>
      <div className="mt-6 max-w-md text-center lg:mt-0 lg:max-w-xl lg:flex-1 lg:text-left">
        <p className="font-display text-[0.6rem] text-accent-strong uppercase tracking-[0.5em] sm:text-xs">
          {tagline}
        </p>
        <h2
          className="mt-4 font-display text-3xl text-ash-50 leading-snug tracking-wide sm:text-4xl lg:text-[2.4rem] lg:leading-[1.15]"
          style={{ fontWeight: 500 }}
        >
          {name}
        </h2>
        <p className="mt-5 font-body text-ash-300 text-base leading-relaxed sm:text-lg lg:mt-6">
          {body}
        </p>
        <p className="mt-6 font-display text-ash-500 text-xs italic tracking-wide sm:text-sm">
          {closing}
        </p>
      </div>
    </section>
  );
}

function CodexFooter() {
  const { t } = useTranslation();
  const lines = t('codex.footer.closingNote').split('\n');
  return (
    <section className="flex flex-col items-center px-6 py-16 text-center sm:px-10 sm:py-20 lg:py-24">
      <div className="hairline mb-12 h-px w-32" />
      <p className="max-w-xl font-display text-ash-100 text-2xl leading-snug tracking-wide sm:text-3xl">
        {lines.map((line, i) => (
          <span key={line}>
            {line}
            {i < lines.length - 1 && <br />}
          </span>
        ))}
      </p>
      <Link
        to="/coming-soon"
        className="mt-12 inline-block rounded-sm border border-accent/60 bg-paper-900/60 px-8 py-4 font-display text-ash-100 text-sm uppercase tracking-[0.34em] transition hover:border-accent-strong/80 hover:bg-paper-700/60 hover:text-ash-50 sm:px-10 sm:tracking-[0.36em]"
      >
        {t('codex.footer.cta')}
      </Link>
      <Link
        to="/"
        className="mt-8 font-display text-[0.7rem] text-ash-400 uppercase tracking-[0.4em] transition hover:text-ash-200 sm:text-xs"
      >
        ← {t('codex.footer.backToLanding')}
      </Link>
      <a
        href="https://instagram.com/athrelaris"
        target="_blank"
        rel="noreferrer"
        className="mt-6 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.4em] transition hover:text-ash-300"
      >
        @athrelaris
      </a>
    </section>
  );
}
